import * as _ from 'lodash';
import moment from 'moment';
import type { ReactNode } from 'react';

import Button from '../../generic/Button/Button';
import Icon, { icons, IconText } from '../../generic/Icon';
import Link from '../../generic/Link/Link';
import SupportLink from '../../generic/Link/SupportLink';
import Loader from '../../generic/Loader';
import type { AppDisplay } from '../../../../shared/vcs/githubApp/apps';
import type { APIError, Repository } from '../../../types';

import BranchDropdown from './BranchDropdown';
import ConnectToServiceFallback from './Fallbacks/ConnectToServiceFallback';
import InstallGithubAppFallback from './Fallbacks/InstallGithubAppFallback';
import useIsGithubApp from './hooks/useIsGithubApp';
import HybridProjectsTable from './HybridProjectsTable';
import RepositoryPagination from './RepositoryPagination';
import type { OnImportRepositoryFn, OnSelectRepositoryFn } from './HybridProjectsTable/types';

export type OnBranchChangeFn = (repo: Repository, newBranch: string) => void;
export type OnRowRedirectFn = (projectId: string) => void;

type ServiceImportRepositoriesProps = {
  repositories: Repository[];
  totalCount?: number;
  /** Whether the API reports more pages; threaded to the no-total pagination fallback. */
  hasMore?: boolean;
  selectedRepos: Repository[];
  page: number;
  pageSize: number;
  isConnected: boolean;
  hasInstallations: boolean;
  isLoading: boolean;
  error?: APIError | string;
  onSetPage: (page: number) => void;
  onSetPageSize: (pageSize: number) => void;
  onBranchChange: OnBranchChangeFn;
  onRowRedirect: OnRowRedirectFn;
  onSelectRepository: OnSelectRepositoryFn;
  onImportRepository: OnImportRepositoryFn;
  onConnectToService: () => void;
  /**
   * Install action for the GitHub App CTA. When omitted, the fallback uses the legacy
   * GitHub App install popup; the vcs-service import flow passes its own handler.
   */
  onInstallGithubApp?: () => void;
  /**
   * Name + logo for the install CTA. When omitted, the fallback uses generic FOSSA
   * branding; the vcs-service flow passes the specific app's display data.
   */
  githubAppDisplay?: AppDisplay;
};

const EmptyRepoPanel = ({ children }: { children: ReactNode }) => (
  <div className="panel text-center empty-repo-panel">
    {children}
  </div>
);

const ServiceImportRepositories = ({
  isConnected,
  hasInstallations,
  isLoading,
  error,
  repositories,
  totalCount,
  hasMore,
  selectedRepos,
  page,
  pageSize,
  onSetPage,
  onSetPageSize,
  onBranchChange,
  onRowRedirect,
  onSelectRepository,
  onImportRepository,
  onConnectToService,
  onInstallGithubApp,
  githubAppDisplay,
}: ServiceImportRepositoriesProps) => {
  const isGithubApp = useIsGithubApp();

  let repoPanelContent;
  if (!isConnected) {
    repoPanelContent = isGithubApp ? (
      <ConnectToServiceFallback onConnectToService={onConnectToService} />
    ) : (
      <p>Connect to instance to see repos</p>
    );
  } else if (!hasInstallations && isGithubApp) {
    repoPanelContent = <InstallGithubAppFallback onInstall={onInstallGithubApp} appDisplay={githubAppDisplay} />;
  } else if (isLoading) {
    repoPanelContent = <Loader />;
  } else if (error) {
    repoPanelContent = <p>{`Error loading repositories: ${error}`}</p>;
  } else if (!repositories.length && page > 1) {
    repoPanelContent = (
      <>
        <h2>No repositories found</h2>
        <p>Oops! Looks like that was the last page.</p>
        <p>
          <Button onClick={() => onSetPage(page - 1)}>Click here to go back.</Button>
        </p>
      </>
    );
  } else if (!repositories.length) {
    repoPanelContent = (
      <>
        <h2>No repositories found</h2>
        <div>
          We could not find any repositories in this account. Please <SupportLink className="highlight">contact support</SupportLink> for assistance.
        </div>
      </>
    );
  }

  if (repoPanelContent) {
    return <EmptyRepoPanel>{repoPanelContent}</EmptyRepoPanel>;
  }

  if (isGithubApp) {
    return (
      <>
        <HybridProjectsTable
          onBranchChange={onBranchChange}
          repositories={repositories}
          selectedRepos={selectedRepos}
          onSelectRepository={onSelectRepository}
          onImportRepository={onImportRepository}
        />
        <RepositoryPagination
          repositories={repositories}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          hasMore={hasMore}
          onSetPage={onSetPage}
          onSetPageSize={onSetPageSize}
        />
      </>
    );
  }
  return (
    <>
      <div className="panel" style={{ overflow: 'visible' }}>
        <table className="table project-table table-striped">
          <thead>
            <tr>
              <th />
              <th>Title</th>
              <th>Branch</th>
              <th>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {repositories.map((row, i) =>
              <RepositoryRow
                key={row.id || i}
                row={row}
                selectedRepos={selectedRepos}
                onBranchChange={onBranchChange}
                onRowRedirect={onRowRedirect}
                onSelectRepository={onSelectRepository}
              />)}
          </tbody>
        </table>
      </div>
      <RepositoryPagination
        repositories={repositories}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        hasMore={hasMore}
        onSetPage={onSetPage}
        onSetPageSize={onSetPageSize}
      />
    </>);
};

type RepositoryRowProps = {
  row: Repository;
  selectedRepos: Repository[];
  onBranchChange: OnBranchChangeFn;
  onRowRedirect: OnRowRedirectFn;
  onSelectRepository: OnSelectRepositoryFn;
};

const RepositoryRow = ({
  row,
  selectedRepos,
  onBranchChange,
  onRowRedirect,
  onSelectRepository,
}: RepositoryRowProps) => {
  const isImported = !!row.project;
  const hasNoBranches = !row.branches || _.isEmpty(row.branches);
  const isSelected = !!selectedRepos.find(repo => repo.locator === row.locator);

  let onRowClick: () => void;
  if (isImported) {
    onRowClick = () => onRowRedirect(row.project?.locator || '');
  } else if (hasNoBranches) {
    onRowClick = _.noop;
  } else {
    onRowClick = () => onSelectRepository(row);
  }

  return (
    <tr
      className={
        (isSelected ? 'selected ' : '') +
        (!isImported ? '' : 'row-success ') +
        (hasNoBranches ? ' row-disabled' : '')
      }
      onClick={(e) => {
        // Opening the branch dropdown calls e.preventDefault(). We
        // catch that here to avoid selecting the current repository.
        if (e.defaultPrevented) {
          return;
        }

        onRowClick();
      }}
    >
      <td className="text-center">
        {!hasNoBranches && (
          isImported ? (
            <Icon icon={icons.externalLink} />
          ) : (
            <input
              type="checkbox"
              checked={isSelected}
            />
          )
        )}
      </td>
      <td>
        <span className="margin-right-xs">
          {row.isFork && <Icon icon={icons.codeFork} />}
        </span>
        <Link
          to={row.url}
          openInNewTab
        >
          <b>{row.title}</b>
        </Link>
          &nbsp;&nbsp;
        {row.isPrivate
          && (
            <span className="badge">
              <IconText
                icon={icons.lock}
                text="Private"
              />
            </span>
          )}
      </td>
      <td>
        { isImported
          ? (
            <small>
              <IconText
                icon={icons.checkCircle}
                text={`Imported: ${moment(row.project?.createdAt).fromNow()}`}
              />
            </small>
            )
          : (!hasNoBranches && <BranchDropdown onBranchChange={onBranchChange} repo={row} />)}
      </td>
      <td>
        {row.updated ? <i>({moment(row.updated).fromNow()})</i> : ''}
      </td>
    </tr>
  );
};

export default ServiceImportRepositories;
